import apiClient from '@/shared/api/client';
import { CreateAlertFormValues, AlertListItem, PaginatedAlerts } from '../types';

export interface AlertCreatedResponse {
  id: string;
}

interface AlertPayload {
  address: string;
  latitude: number;
  longitude: number;
  maxDistance: number | null;
  materialId: string;
  materialCategory: string;
  materialSubcategory: string;
  condition: string | null;
  availabilityEnabled: boolean;
  startDate: string | null;
  endDate: string | null;
}

function buildAlertPayload(data: CreateAlertFormValues): AlertPayload {
  return {
    address: data.address,
    latitude: data.latitude as number,
    longitude: data.longitude as number,
    maxDistance: data.distanceEnabled ? data.maxDistance : null,
    materialId: data.materialId,
    materialCategory: data.materialCategory,
    materialSubcategory: data.materialSubcategory,
    condition: data.condition || null,
    availabilityEnabled: data.availabilityEnabled,
    startDate:
      data.availabilityEnabled && data.availableFrom
        ? new Date(data.availableFrom).toISOString()
        : null,
    endDate:
      data.availabilityEnabled && data.availableTo
        ? new Date(data.availableTo).toISOString()
        : null,
  };
}

export async function createAlert(data: CreateAlertFormValues): Promise<AlertCreatedResponse> {
  const payload = buildAlertPayload(data);
  return apiClient.post<AlertCreatedResponse>('/alerts', payload);
}

export async function getAlertById(id: string): Promise<AlertListItem> {
  return apiClient.get<AlertListItem>(`/alerts/${id}`);
}

export async function updateAlert(id: string, data: CreateAlertFormValues): Promise<AlertListItem> {
  const payload = buildAlertPayload(data);
  return apiClient.post<AlertListItem>(`/alerts/${id}`, payload);
}

export async function getAlerts(page = 1, limit = 3): Promise<PaginatedAlerts> {
  return apiClient.get<PaginatedAlerts>('/alerts', { params: { page, limit } });
}

export async function disableAlert(id: string): Promise<void> {
  return apiClient.post<void>(`/alerts/${id}/disable`);
}

export async function getMatchingOffers(
  page = 1,
  limit = 10
): Promise<import('../types').PaginatedMatchingProducts> {
  return apiClient.get<import('../types').PaginatedMatchingProducts>('/alerts/matching-offers', {
    params: { page, limit },
  });
}

export async function getUnreadAlertCount(): Promise<{ count: number }> {
  return apiClient.get<{ count: number }>('/notifications/unread-count');
}

export async function markAllAlertsRead(): Promise<void> {
  await apiClient.post<{ updated: number }>('/notifications/mark-all-read');
}
